/**
 * The trial call-to-action for client-rendered routes (blog article, country
 * page, /prices). Server-rendered pages carry the same block from
 * backend/src/util/trial-cta.ts; both read the same i18n keys and the same
 * `trial.enabled` switch. Trust-before-payment framing, not a giveaway.
 */
import { t } from './i18n';
import { escapeHtml } from './utils';
import { getConfig, isPrimed } from './config';

export function trialEnabled(): boolean {
  // Before the first /api/config answer the SPA has no opinion; the trial is
  // on by default and a later poll removes the block if the operator has it
  // off. Hiding it until the poll lands would flash the page on every route.
  return isPrimed() ? getConfig().trial.enabled : true;
}

/** The trial eSIM's credit. Before the first /api/config answer: the server's
 *  default (db/settings.ts trialCreditMicroSync), which is what SSR printed. */
export function trialCreditMicro(): number {
  return isPrimed() ? getConfig().trial.creditMicroUsdt : 500_000;
}

/** Megabytes the trial credit buys at a given per-MB rate (money, not MB, is what is granted). */
export function trialMbFor(rateUsdtPerMb: number): number {
  const credit = trialCreditMicro();
  if (!(rateUsdtPerMb > 0) || !(credit > 0)) return 0;
  return Math.floor(credit / 1_000_000 / rateUsdtPerMb);
}

export function trialCtaHtml(opts: { country?: string; rate?: string; rateUsdtPerMb?: number } = {}): string {
  if (!trialEnabled()) return '';
  const mb = opts.rateUsdtPerMb ? trialMbFor(opts.rateUsdtPerMb) : 0;
  const interpolated = !!(opts.country && opts.rate && mb > 0);
  const body = interpolated
    ? t('trialCta.countryBody', { country: opts.country!, rate: opts.rate!, mb: String(mb) })
    : t('trialCta.body');
  return `<section class="trial-cta" data-trial-cta>
    <div class="container">
      <div class="trial-cta__card">
        <div class="section__kicker" data-i18n="trialCta.kicker">${escapeHtml(t('trialCta.kicker'))}</div>
        <h2 class="trial-cta__title" data-i18n="trialCta.title">${escapeHtml(t('trialCta.title'))}</h2>
        <p class="trial-cta__body"${interpolated ? '' : ' data-i18n="trialCta.body"'}>${escapeHtml(body)}</p>
        <a class="btn btn--accent btn--lg" href="/dashboard" data-i18n="trialCta.button">${escapeHtml(t('trialCta.button'))}</a>
        <p class="trial-cta__note" data-i18n="trialCta.note">${escapeHtml(t('trialCta.note'))}</p>
      </div>
    </div>
  </section>`;
}
